// @ts-strict-ignore
import capitalize from 'lodash/capitalize';

import {ChartType, ProjectFeatureFlag} from 'common/report/constants';
import type {CurrentProject} from 'common/report/model-resources/projects';
import {LineChartYAxisOptions, XAxisOptions} from 'common/charts/visualization/line-chart/types';
import {AxisUnitOptions, InsightsBookmarkParams} from 'common/report/insights/util/types';
import {getCountType} from 'common/report/insights/charts/funnels-step-chart/bookmark-controller/util';
import {CountType} from 'common/report/funnels/models/types';
import {isConversionOrRetentionRateMetricForAxis} from 'common/report/insights/models/utils';
import {ProjectDetails} from 'common/report/stores/project-details';
import {AxisAssignment} from 'common/charts/types';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import {MetricType} from 'common/query-builder/query-block/constants';
import {pluralize} from 'common/util/string';
import {getAddictionUnit} from 'common/report/retention/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {Store} from 'common/report/stores/store';
import {
  getFinalDisplayOptions,
  getLineChartYAxisOptionsFromPropertyDisplayOptions,
} from 'common/report/insights/charts/util';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {PropertyDisplayOptions} from 'apps/lexicon/src/models/property-definition';

export function isChartCustomizationEnabled(currentProject: ProjectDetails | CurrentProject | null): boolean {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.ChartCustomization) ?? false;
}

export function getAxisUnitPrecision(axisUnit: AxisUnitOptions) {
  return axisUnit?.precision ?? undefined;
}

/**
 * Returns the common display options for an array of metrics. An axis will inherit metric units from all the common
 * options for the metrics on that axis.
 */
export const getMetricDefaultDisplayOptions = (
  clauses: Array<Partial<PropertyDisplayOptions>>,
): Partial<LineChartYAxisOptions> => {
  const displayKeyToAllValues: Partial<Record<keyof PropertyDisplayOptions, Set<string | boolean | number>>> = {};
  clauses.forEach((clause) =>
    Object.entries(clause).forEach((entry) => {
      const [k, v] = entry;
      if (!displayKeyToAllValues[k]) {
        displayKeyToAllValues[k] = new Set();
      }
      displayKeyToAllValues[k].add(v);
    }),
  );
  const result: Partial<PropertyDisplayOptions> = {};
  Object.keys(displayKeyToAllValues).map((k) => {
    if (displayKeyToAllValues[k].size === 1) {
      result[k] = displayKeyToAllValues[k].values().next().value;
    }
  });
  return getLineChartYAxisOptionsFromPropertyDisplayOptions(result);
};

export function getYAxisDefaultOptions(
  bookmarkParams: InsightsBookmarkParams,
  axis: AxisAssignment,
): LineChartYAxisOptions {
  // Populate options with options inherited from metrics.
  const showClauses = bookmarkParams.sections.show;
  const axisClauses = showClauses
    .map((x) => getFinalDisplayOptions(x, eventPropertyDefinitionsStore))
    .filter((x) =>
      axis === AxisAssignment.Secondary ? x.axis === AxisAssignment.Secondary : x.axis !== AxisAssignment.Secondary,
    );
  const options = getMetricDefaultDisplayOptions(axisClauses);

  let usePercentAxisUnit = false;
  // Handle adding % for lift comparison and conversion/retention rate
  usePercentAxisUnit ||= `liftComparison` in bookmarkParams && Boolean(bookmarkParams.liftComparison);
  usePercentAxisUnit ||= isConversionOrRetentionRateMetricForAxis(bookmarkParams, axis);

  if (bookmarkParams.displayOptions.chartType === ChartType.FunnelSteps) {
    // All funnel steps treat 0 as the soft min
    options.softMin = 0;
    // If it's conversion we treat the max as 1 and unit as "%"
    if (getCountType(bookmarkParams.sections.show) === CountType.ConversionRate) {
      usePercentAxisUnit = true;
      options.softMax = 1;
    }
  }

  if (usePercentAxisUnit) {
    if (options.axisUnit) {
      options.axisUnit.suffix = `%`;
    } else {
      options.axisUnit = {suffix: `%`};
    }
  }

  return options;
}

export function getXAxisDefaultOptions(
  bookmarkParams: InsightsBookmarkParams,
  dataGroupStore: Store<Obj<DataGroup>>,
): XAxisOptions {
  const activeDataGroupId = bookmarkParams.sections.globalDataGroupId;
  const activeGroup = activeDataGroupId && dataGroupStore.value ? dataGroupStore.value[activeDataGroupId] : null;
  if (bookmarkParams.displayOptions.chartType === ChartType.RetentionCurve) {
    return {title: {placeholder: getRetentionCurveChartDescription(bookmarkParams, activeGroup)}};
  }
  return {};
}

function getRetentionCurveChartDescription(bookmarkParams: InsightsBookmarkParams, activeGroup: DataGroup) {
  const showClause = bookmarkParams.sections.show[0] as BehaviorShowClause;
  const isAddiction = showClause.behavior.type === MetricType.RetentionFrequency;
  const dataGroupName = pluralize(activeGroup?.displayName || `Users`, true);

  if (isAddiction) {
    const retentionCumulative = showClause.measurement.retentionCumulative;
    const dateUnit = bookmarkParams.sections.time[0].unit;
    if (!retentionCumulative) {
      return `The number of ${getAddictionUnit(dateUnit)} in a ${dateUnit} ${dataGroupName} did A`;
    } else {
      return `${capitalize(dataGroupName)} did A at least X ${dateUnit === `day` ? `hours` : `days`} in a ${dateUnit}`;
    }
  } else {
    const {retentionUnit, retentionAlignmentType, retentionUnboundedMode} = showClause.behavior;
    const alignmentModifier = retentionAlignmentType === `birth` ? `` : `calendar `;
    if (retentionUnboundedMode === `carry_back`) {
      return `X and more ${alignmentModifier}${retentionUnit}s later your ${dataGroupName} came back and did B.`;
    } else if (retentionUnboundedMode === `carry_forward`) {
      return `X and more ${alignmentModifier}${retentionUnit}s before your ${dataGroupName} came back and did B.`;
    } else if (retentionUnboundedMode === `consecutive_forward`) {
      return `Returns every ${alignmentModifier}${retentionUnit}s after the birth event.`;
    } else {
      return `The number of exact ${alignmentModifier}${retentionUnit}s later your ${dataGroupName} came back and did B.`;
    }
  }
}
